(function () {
  class EbQuickAddModal extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.eb-quick-add__panel');
      this.content = this.querySelector('[data-quick-add-content]');
      this.bind();
    }

    bind() {
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-eb-quick-add]');
        if (trigger) this.handleTrigger(event, trigger);
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-quick-add-close]')) this.close();
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-quick-add-option]')) this.updateVariant();
      });

      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-quick-add-form]')) this.submit(event);
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') this.trapFocus(event);
      });
    }

    async handleTrigger(event, trigger) {
      event.preventDefault();
      event.stopPropagation();
      this.lastTrigger = trigger;
      const variantId = Number(trigger.dataset.variantId);
      if (trigger.dataset.singleVariant === 'true' && variantId) {
        await this.addToCart([{ id: variantId, quantity: 1 }], trigger);
        return;
      }
      await this.openProduct(trigger.dataset.productHandle, trigger.dataset.productUrl);
    }

    async openProduct(handle, fallbackUrl) {
      if (!handle) return;
      this.product = await fetch(`/products/${encodeURIComponent(handle)}.js`).then((response) => response.json()).catch(() => null);
      if (!this.product) {
        window.location.href = fallbackUrl || `/products/${handle}`;
        return;
      }
      this.selectedVariant = this.product.variants.find((variant) => variant.available) || this.product.variants[0];
      this.render();
      this.open();
    }

    render() {
      if (!this.content || !this.product) return;
      const image = this.selectedVariant?.featured_image?.src || this.product.featured_image || this.product.images?.[0] || '';
      this.content.innerHTML = `
        <form class="eb-quick-add-product" data-quick-add-form>
          <div class="eb-quick-add-product__media" data-quick-add-media>
            ${image ? `<img src="${this.escape(image)}" alt="${this.escape(this.product.title)}">` : '<span></span>'}
          </div>
          <div class="eb-quick-add-product__body">
            <h2 id="QuickAddTitle">${this.escape(this.product.title)}</h2>
            <p class="eb-quick-add-product__price" data-quick-add-price>${this.money(this.selectedVariant?.price || this.product.price)}</p>
            <div class="eb-quick-add-options">
              ${this.product.options.map((option, optionIndex) => `
                <fieldset>
                  <legend>${this.escape(option.name)}</legend>
                  <div class="eb-quick-add-options__values">
                    ${option.values.map((value) => `
                      <label>
                        <input type="radio" name="option-${optionIndex}" value="${this.escape(value)}" data-quick-add-option data-option-index="${optionIndex}" ${this.selectedVariant?.options?.[optionIndex] === value ? 'checked' : ''}>
                        <span>${this.escape(value)}</span>
                      </label>
                    `).join('')}
                  </div>
                </fieldset>
              `).join('')}
            </div>
            <button class="eb-btn eb-btn--primary eb-btn--full" type="submit" data-quick-add-submit ${this.selectedVariant?.available ? '' : 'disabled'}>
              ${this.escape(this.dataset.addLabel)} · ${this.money(this.selectedVariant?.price || this.product.price)}
            </button>
            <a class="eb-quick-add-product__details" href="${this.escape(this.product.url || `/products/${this.product.handle}`)}">${this.escape(this.dataset.detailsLabel)} →</a>
            <p class="eb-quick-add-product__status" data-quick-add-status role="status"></p>
          </div>
        </form>
      `;
    }

    updateVariant() {
      const options = [...this.querySelectorAll('[data-quick-add-option]:checked')]
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((input) => input.value);
      const variant = this.product?.variants?.find((item) => item.options.every((value, index) => value === options[index]));
      this.selectedVariant = variant || null;
      const submit = this.querySelector('[data-quick-add-submit]');
      const price = this.querySelector('[data-quick-add-price]');
      const image = this.querySelector('[data-quick-add-media]');
      if (price) price.textContent = variant ? this.money(variant.price) : '';
      if (submit) {
        submit.disabled = !variant?.available;
        submit.textContent = variant?.available ? `${this.dataset.addLabel} · ${this.money(variant.price)}` : this.dataset.unavailableLabel;
      }
      const imageSrc = variant?.featured_image?.src || this.product?.featured_image;
      if (image && imageSrc) image.innerHTML = `<img src="${this.escape(imageSrc)}" alt="${this.escape(this.product.title)}">`;
    }

    async submit(event) {
      event.preventDefault();
      const status = this.querySelector('[data-quick-add-status]');
      if (!this.selectedVariant) {
        if (status) status.textContent = this.dataset.selectLabel || '';
        return;
      }
      try {
        const lines = [{ id: Number(this.selectedVariant.id), quantity: 1 }];
        await this.addToCart(lines);
        this.close();
      } catch (error) {
        if (status) status.textContent = this.dataset.errorLabel || '';
      }
    }

    async addToCart(lines, trigger) {
      const payload = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines }),
      }).then((response) => {
        if (!response.ok) throw response;
        return response.json();
      });
      (trigger || this).dispatchEvent(new CustomEvent('eb:cart:add', { bubbles: true, composed: true, detail: { lines, payload } }));
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      window.ebScrollLock?.lock();
      window.setTimeout(() => this.panel?.focus(), 30);
    }

    close() {
      const wasOpen = this.classList.contains('is-open');
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (wasOpen) window.ebScrollLock?.unlock();
      if (wasOpen) this.dispatchEvent(new CustomEvent('eb:quick-add:close', { bubbles: true }));
      this.lastTrigger?.focus?.();
    }

    trapFocus(event) {
      const focusable = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    money(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(amount);
      } catch (error) {
        return amount.toFixed(2);
      }
    }

    escape(value) {
      return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[char]);
    }
  }

  if (!customElements.get('eb-quick-add-modal')) customElements.define('eb-quick-add-modal', EbQuickAddModal);
})();
